import $ from "jquery";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../../hooks/useRequiredFields";
import useFocusedRowHighlight from "../../../../../hooks/useFocusedRowHighlight";
import Http from "../../../../Common/Http";
import IsLogin from "../../../../Common/IsLogin";
import { ProgressSpinner } from "primereact/progressspinner";
import { set } from "date-fns";

const Create = () => {
  const { checkRequiredFields } = useRequiredFields();
  const { getRowProps } = useFocusedRowHighlight();
  const navigate = useNavigate();

  const [inputs, setInputs] = useState({
    receipt_no: "",
    production_work_order_id: "",
    date: "",
    receive_date: "",
    entry_date: new Date().toISOString().split("T")[0],
    copy_type: "",
    purchase_type: "",
    from_department: "",
    to_department: "",
    status: "requested",
    remarks: "",
    kiln_type: "",
  });

  const [list, setList] = useState([]);
  const [error, setError] = useState({});
  const [planningOptions, setPlanningOptions] = useState([]);
  const [role, setRole] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const [searchProducts, setSearchProducts] = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [showPagination, setShowPagination] = useState(true);
  const [itemLoaded, setItemLoaded] = useState(false);

  const allItemsWithInputRef = useRef(new Map());
  const [allItemsWithInput, setAllItemsWithInput] = useState(new Map());
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const searchTimeoutRef = useRef(null);
  const roleRef = useRef("");
  const originalListRef = useRef([]);
  const paginationRef = useRef({ current_page: 1, per_page: 15, total: 0, last_page: 1 });
    const [isSubmitting, setIsSubmitting] = useState(false);
  

  const updateAllItemsWithInput = (updaterFn) => {
    const newMap = updaterFn(allItemsWithInputRef.current);
    allItemsWithInputRef.current = newMap;
    setAllItemsWithInput(newMap);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const resRole = await Http.get("/staff-type-check");
      roleRef.current = resRole.data;
      setRole(resRole.data);

      const resPlanning = await Http.get("/planning");
      const planningData = resPlanning.data;
      setPlanningOptions(planningData);

      if (planningData.length > 0) {
        const firstPlanning = planningData[0];
        setInputs((prevInputs) => ({
          ...prevInputs,
          planning_id: firstPlanning.id,
        }));
        handlePlanningChange(firstPlanning.id);
      }

      const resReceiptNo = await Http.get(
        "/last-code-increment-by-one/production_finished_goods/receipt_no/P"
      );
      setInputs((prevInputs) => ({
        ...prevInputs,
        receipt_no: resReceiptNo.data,
      }));
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
      setItemLoading(false);
    }
  };

  useEffect(() => {
    $("body").addClass("collapse-menu");
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleInputChange = (e, itemId, available_qty = null) => {
    let { name, value } = e.target;
    let inputValue = value === "" ? 0 : parseFloat(value);

    setList((prevList) => {
      const currentItem = prevList.find((it) => it.id === itemId);
      if (!currentItem) return prevList;

      const productId = currentItem.product_id;
      const shapeId = currentItem.shape_id;
      const glazeId = currentItem.glaze_id;
      const currentRole = roleRef.current;

      const tempList = prevList.map((it) =>
        it.id === itemId ? { ...it, [name]: inputValue } : it
      );

      const sameGroup = (it) =>
        it.product_id === productId &&
        (it.shape_id ?? 0) === (shapeId ?? 0) &&
        (currentRole === "gk" ? (it.glaze_id ?? 0) === (glazeId ?? 0) : true);

      const newGroupTransferQty = tempList
        .filter(sameGroup)
        .reduce((sum, it) => sum + (parseFloat(it.transfer_qty) || 0), 0);
      const newGroupBeforeLossQty = tempList
        .filter(sameGroup)
        .reduce((sum, it) => sum + (parseFloat(it.before_production_loss_qty) || 0), 0);
      const newGroupAfterLossQty = tempList
        .filter(sameGroup)
        .reduce((sum, it) => sum + (parseFloat(it.after_production_loss_qty) || 0), 0);

      const proposedAvailable =
        (currentItem.current_stock || 0) -
        (name === "return_qty" ? inputValue : currentItem.return_qty || 0) -
        newGroupTransferQty -
        newGroupAfterLossQty -
        newGroupBeforeLossQty;

      if (
        ["transfer_qty", "before_production_loss_qty", "after_production_loss_qty", "return_qty"].includes(name) &&
        proposedAvailable < 0
      ) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "This value cannot be less than 0.",
        });
        return prevList;
      }

      const updatedList = prevList.map((it) =>
        it.id === itemId ? { ...it, [name]: inputValue } : it
      );

      const updatedItem = updatedList.find((it) => it.id === itemId);

      const groupTransferQty = updatedList
        .filter(sameGroup)
        .reduce((sum, it) => sum + (parseFloat(it.transfer_qty) || 0), 0);
      const groupBeforeLossQty = updatedList
        .filter(sameGroup)
        .reduce((sum, it) => sum + (parseFloat(it.before_production_loss_qty) || 0), 0);
      const groupAfterLossQty = updatedList
        .filter(sameGroup)
        .reduce((sum, it) => sum + (parseFloat(it.after_production_loss_qty) || 0), 0);

      const closingBalance =
        (updatedItem.current_stock || 0) -
        (updatedItem.return_qty || 0) -
        groupTransferQty -
        groupAfterLossQty -
        groupBeforeLossQty -
        (updatedItem.total_loss_qty || 0);

      const finalList = updatedList.map((it) =>
        it.id === itemId ? { ...it, closing_balance: closingBalance } : it
      );

      const finalItem = finalList.find((it) => it.id === itemId);

      const newMap = new Map(allItemsWithInputRef.current);
      newMap.set(itemId, { ...finalItem });
      allItemsWithInputRef.current = newMap;
      setAllItemsWithInput(new Map(newMap));
      originalListRef.current = originalListRef.current.map((it) =>
        it.id === itemId ? { ...finalItem } : it
      );
      setOriginalList([...originalListRef.current]);

      return finalList;
    });
  };

  const formSubmit = () => {
    checkRequiredFields();
    let allItemsArray = Array.from(allItemsWithInputRef.current.values());

    if (role === "glazing" || role === "gk") {
      if (!inputs.kiln_type) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `Kiln Type is required for ${role}.`,
        });
        return;
      }
      allItemsArray = allItemsArray.map((item) => ({
        ...item,
        kiln_type: inputs.kiln_type,
      }));
    }

    const formData = { ...inputs, items: allItemsArray };

    // ADDED: confirmation prompt before submitting
  Swal.fire({
    title: "Are you sure?",
    text: "Do you want to submit this transfer?",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Yes, submit",
    cancelButtonText: "Cancel",
  }).then((result) => {
    // if (!result.isConfirmed) return;
    if (!result.isConfirmed) {
      setIsSubmitting(false); // ADDED: reappear on cancel
      return;
    }

    Http.post(`/production-transfer`, formData)
      .then((res) => {
        navigate("/transfer-art", {
          state: { message: res.data.message, type: "success" },
        });
      })
      // .catch((e) => {
      //   setError(e.response.data.errors);
      // });
      .catch((e) => {
      const responseData = e.response?.data;
      const errors = responseData?.errors;

      // Stock validation errors come back as a plain array of message strings
      if (Array.isArray(errors) && errors.length > 0) {
        Swal.fire({
          icon: "error",
          title: responseData.message || "Validation Error",
          html: `<ul style="text-align:left; margin:0; padding-left:20px;">
                   ${errors.map((msg) => `<li>${msg}</li>`).join("")}
                 </ul>`,
        });
        return;
      }

      // Fallback: laravel-style field validation errors (object, not array)
      setError(errors || {});
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: responseData?.message || "Something went wrong. Please check the form.",
      });
    });
    });

  };

  const getGroupTransferQty = useCallback(
    (productId, shapeId, glazeId) =>
      list.reduce((sum, it) => {
        const sameProduct = it.product_id === productId;
        const sameShape = (it.shape_id ?? 0) === (shapeId ?? 0);
        const sameGlaze = role === "gk" ? (it.glaze_id ?? 0) === (glazeId ?? 0) : true;
        return sum + (sameProduct && sameShape && sameGlaze ? parseFloat(it.transfer_qty) || 0 : 0);
      }, 0),
    [list, role]
  );

  const getGroupBeforeLossQty = useCallback(
    (productId, shapeId, glazeId) =>
      list.reduce((sum, it) => {
        const sameProduct = it.product_id === productId;
        const sameShape = (it.shape_id ?? 0) === (shapeId ?? 0);
        const sameGlaze = role === "gk" ? (it.glaze_id ?? 0) === (glazeId ?? 0) : true;
        return sum + (sameProduct && sameShape && sameGlaze ? parseFloat(it.before_production_loss_qty) || 0 : 0);
      }, 0),
    [list, role]
  );

  const getGroupAfterLossQty = useCallback(
    (productId, shapeId, glazeId) =>
      list.reduce((sum, it) => {
        const sameProduct = it.product_id === productId;
        const sameShape = (it.shape_id ?? 0) === (shapeId ?? 0);
        const sameGlaze = role === "gk" ? (it.glaze_id ?? 0) === (glazeId ?? 0) : true;
        return sum + (sameProduct && sameShape && sameGlaze ? parseFloat(it.after_production_loss_qty) || 0 : 0);
      }, 0),
    [list, role]
  );

  const mapItemFromApi = (item) => {
    const existingItem = allItemsWithInputRef.current.get(item.id);
    const baseItem = {
      id: item?.id,
      product_id: item?.product_id,
      product_name: item?.product?.name,
      qty: item?.qty || 0,
      total_transfer: item?.total_transfer,
      shape_id: item?.shape_id,
      shape: item?.shape?.name,
      production_work_order: item?.production_work_order?.receipt_no,
      company: item?.production_work_order?.company?.name,
      product_code: item?.product?.code,
      weight: item?.weight,
      load_qty: 0,
      before_production_loss_qty: 0,
      return_qty: 0,
      after_production_loss_qty: 0,
      current_stock: item?.current_stock || 0,
      closing_balance: item?.qty || 0,
      remarks_delivery: "",
      total_received_qty: item?.total_received_qty,
      total_transfer_qty: item?.total_transfer_qty,
      glaze_color_name: item?.production_work_order_item?.color?.colour_name,
      glaze_color: item?.production_work_order_item?.color?.id,
      decoration_code: item?.production_work_order_item?.decoration_code,
      decoration_name: item?.production_work_order_item?.decoration_name,
      glaze_id: item?.production_work_order_item?.glaze_id,
      total_loss_qty: item?.total_loss_qty || 0,
      status: item?.production_work_order?.status,
      available_qty:
        parseFloat(item?.total_received_qty || 0) -
        parseFloat(item?.total_transfer_qty || 0),
      kiln_type: "",
    };
    return existingItem ? { ...baseItem, ...existingItem } : baseItem;
  };

  const handlePlanningChange = async (planningId, page = 1, productName = "") => {
    if (!planningId) return;
    setLoading(true);
    setItemLoading(true);

    setInputs((prevInputs) => ({ ...prevInputs, planning_id: planningId }));

    try {
      const response = await Http.get(
        `/planning/${planningId}?page=${page}&limit=${paginationRef.current.per_page}&product_name=${productName}`
      );

      const currentRole = roleRef.current;
      const planningItems = response.data.items
        .filter((item) => currentRole === "finishing" ? item?.product?.department_art_id === 2 : true)
        .map(mapItemFromApi);

      setList(planningItems);
      originalListRef.current = planningItems;
      setOriginalList(planningItems);
      paginationRef.current = response.data.pagination;
      setPagination(response.data.pagination);
      setItemLoaded(true);

      const newMap = new Map(allItemsWithInputRef.current);
      planningItems.forEach((item) => newMap.set(item.id, item));
      allItemsWithInputRef.current = newMap;
      setAllItemsWithInput(new Map(newMap));
    } catch (error) {
      console.error("Error fetching products for planning:", error);
      Swal.fire("Error!", "Failed to fetch products for the selected planning.", "error");
    } finally {
      setLoading(false);
      setItemLoading(false);
    }
  };

  useEffect(() => {
    if (inputs.planning_id) {
      handlePlanningChange(inputs.planning_id);
    }
  }, [inputs.planning_id,isInitialLoadComplete]);

  const handleSearchProduct = (e, page = 1) => {
    const searchValue = e.target.value;
    setSearchProducts(searchValue);
    setItemLoading(true);
    setLoading(true);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      debouncedSearch(searchValue, inputs.planning_id, page);
    }, 1000);
  };

  const handlePageChange = (page) => {
    setLoading(true);
    if (page < 1 || page > paginationRef.current.last_page) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (searchProducts?.trim()) {
      debouncedSearch(searchProducts, inputs.planning_id, page);
    } else {
      handlePlanningChange(inputs.planning_id, page);
    }
  };
  const debouncedSearch = async (searchValue, planningId, page = 1) => {
    setLoading(true);
    setItemLoading(true);
    setShowPagination(true);

    if (searchValue.trim() === "") {
      setIsInitialLoadComplete(!isInitialLoadComplete);
      // setShowPagination(true);
      // setTimeout(() => {
      //   setLoading(false);
      //   setItemLoading(false);
      // }, 300);
      return;
    }

    try {
      const response = await Http.get(
        `/planning/${planningId}?page=${page}&limit=${paginationRef.current.per_page}&product_name=${searchValue}`
      );

      const currentRole = roleRef.current;
      const updatedFilteredItems = (response?.data?.items || [])
        .filter((item) => currentRole === "finishing" ? item?.product?.department_art_id === 2 : true)
        .map(mapItemFromApi);

      setList(updatedFilteredItems);
      paginationRef.current = response.data.pagination;
      setPagination(response.data.pagination);
      const newMap = new Map(allItemsWithInputRef.current);
      updatedFilteredItems.forEach((item) => {
        if (!newMap.has(item.id)) newMap.set(item.id, item);
      });
      allItemsWithInputRef.current = newMap;
      setAllItemsWithInput(new Map(newMap));
    } catch (error) {
      console.error("Error searching products:", error);
      setList([]);
    } finally {
      setLoading(false);
      setItemLoading(false);
    }
  };

  const renderPagination = () => {
    const { current_page, last_page } = pagination;
    const pageButtons = [];
    let startPage = Math.max(1, current_page - 2);
    let endPage = Math.min(last_page, startPage + 4);
    if (endPage - startPage < 4) startPage = Math.max(1, endPage - 4);

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          className={`btn btn-sm mx-1 ${i === current_page ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => handlePageChange(i)}
          disabled={i === current_page}
        >{i}</button>
      );
    }

    return (
      <div className="d-flex align-items-center justify-content-center my-3">
        <button className="btn btn-sm btn-outline-secondary mx-1" onClick={() => handlePageChange(1)} disabled={current_page === 1}>First</button>
        <button className="btn btn-sm btn-outline-secondary mx-1" onClick={() => handlePageChange(current_page - 1)} disabled={current_page === 1}>Previous</button>
        {pageButtons}
        <button className="btn btn-sm btn-outline-secondary mx-1" onClick={() => handlePageChange(current_page + 1)} disabled={current_page === last_page}>Next</button>
        <button className="btn btn-sm btn-outline-secondary mx-1" onClick={() => handlePageChange(last_page)} disabled={current_page === last_page}>Last</button>
        <span className="mx-2">Page {current_page} of {last_page}</span>
      </div>
    );
  };

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Production and Deliver</h5>
        </div>
        <div className="am-pagebody row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-11">
            <h6 className="card-body-title mg-sm-b-30">Production and Delivery</h6>

            <div className="row my-1 pt-3 mb-2 fixed-header" style={{ position: "sticky", top: "12%", width: "102.1%", marginBottom: "10px", zIndex: "1002", backgroundColor: "white" }}>
              <div className="col-lg">
                <label>Receipt No</label>
                <input type="text" className="form-control" name="receipt_no" value={inputs.receipt_no} disabled />
              </div>
              <div className="col-lg">
                <label>Planning</label>
                <select className="form-control" value={inputs.planning_id || ""} onChange={(e) => handlePlanningChange(e.target.value)}>
                  <option value="">Select Planning</option>
                  {planningOptions.map((planning) => (
                    <option key={planning.id} value={planning.id}>{planning.receipt_no}</option>
                  ))}
                </select>
              </div>
              <div className="col-lg">
                <label>Shift <i className="fa fa-star required"></i></label>
                <select className="form-control" name="shift" value={inputs.shift} onChange={handleChange} data-required>
                  <option value="">Select Shift</option>
                  <option value="a">A</option>
                  <option value="b">B</option>
                  <option value="c">C</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div className="col-lg">
                <label>Entry Date <i className="fa fa-star required"></i></label>
                <input type="date" className="form-control" name="entry_date" value={inputs.entry_date} onChange={handleChange} data-required />
              </div>
              {(role === "glazing" || role === "gk") && (
                <div className="col-lg">
                  <label>Kiln Type <i className="fa fa-star required"></i></label>
                  <select className="form-control" name="kiln_type" value={inputs.kiln_type} onChange={handleChange} data-required>
                    <option value="">Select Kiln Type</option>
                    <option value="shuttle_kiln">Shuttle Kiln</option>
                    <option value="tunnel_kiln">Tunnel Kiln</option>
                  </select>
                  <span className="text-danger">{error.kiln_type}</span>
                </div>
              )}
            </div>

            <div className="row fixed-header pb-1" style={{ position: "sticky", top: "22%", width: "102.1%", marginBottom: "10px", zIndex: "1000", backgroundColor: "white" }}>
              <div className="col-lg my-2">
                <label>Search Product</label>
                <input type="search" className="form-control" name="search_product" placeholder="Search Product" onChange={(e) => handleSearchProduct(e)} />
              </div>
              <div className="col-lg my-2">
                <label>Remarks</label>
                <textarea className="form-control" name="remarks" value={inputs.remarks} onChange={handleChange} />
              </div>
              {list?.length > 0 && (
                <>
                  {!isSubmitting && (
                    <div className="col-lg mt-5">
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setIsSubmitting(true);
                          formSubmit();
                        }}
                      >
                        Submit
                      </button>
                    </div>
                  )}
                  {isSubmitting && (
                    <div className="col-lg mt-5">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setIsSubmitting(false)}
                      >
                        Regain Submit
                      </button>
                    </div>
                  )}
                  {showPagination && (
                    <div className="col-lg-5 mt-4">{renderPagination()}</div>
                  )}
                </>
              )}
            </div>

            {loading ? (
              <ProgressSpinner />
            ) : (
              <div className="row mt-3">
                <div className="card-solid">
                  <div className="card-header with-border text-center">
                    <h5 className="card-title mb-0">Items in the Production</h5>
                  </div>
                </div>
                <div className="col-lg">
                  <table className="table table-sm" style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead className="fixed-header" style={{ position: "sticky", top: "35%", width: "104%", zIndex: "1000", backgroundColor: "white" }}>
                      <tr>
                        <th>Product</th>
                        <th>Shape</th>
                        <th>Company</th>
                        <th>Planning Qty</th>
                        {role === "bk" && <th>Biscuit Weight</th>}
                        <th>Available</th>
                        {role !== "bk" && role !== "gk" && <th>Transfer</th>}
                        <th>Transfered</th>
                        <th>Received</th>
                        {(role === "bk" || role === "gk" || role === "glazing") && (
                          <>
                            {role !== "glazing" && <th>Load</th>}
                            {role === "gk" && <th>Load Loss</th>}
                            {role === "glazing" && (<><th>Before Glaze Loss</th><th>After Glaze Loss</th></>)}
                            {role !== "glazing" && (<><th>Unload</th><th>Unload Loss</th></>)}
                            <th>Total Loss Qty</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(list.length && itemLoaded) < 1 ? (
                        <tr><td colSpan={13} className="text-center"><ProgressSpinner /></td></tr>
                      ) : (
                        list.map((item, index) => {
                          const rowKey = item?.id ?? index;
                          const rowStatusClass =
                            Number(item?.total_transfer_qty) > Number(item?.qty)
                              ? "bg-warning text-white"
                              : Number(item?.total_transfer_qty) === Number(item?.qty)
                                ? "bg-success text-white"
                                : "";

                          return (
                            <tr
                              key={item.id}
                              {...getRowProps(rowKey, rowStatusClass)}
                              style={{
                                fontStyle: item?.status === "on_test" ? "italic" : undefined,
                                fontWeight: item?.status === "on_test" ? "700" : undefined,
                                border: item?.status === "on_test" ? "2px solid rgb(0, 89, 119)" : undefined,
                              }}
                            >
                              <td>
                                {item.product_name}<br /><br />
                                {(role === "glazing" || role === "gk") && (
                                  <b>Glaze: {item?.glaze_color_name || "N/A"}</b>
                                )}
                              </td>
                              <td>{item.shape}</td>
                              <td>{item.company}<strong>#{item.production_work_order}</strong></td>
                              <td>{item.qty}</td>
                              {role === "bk" && (
                                <td>
                                  <input
                                    type="text"
                                    onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                    className="form-control"
                                    name="biscuit_weight"
                                    value={item.biscuit_weight || ""}
                                    onChange={(e) => handleInputChange(e, item.id)}
                                  />
                                </td>
                              )}
                              <td>
                                {(
                                  Number(item.current_stock || 0) -
                                  Number(item.return_qty || 0) -
                                  Number(getGroupTransferQty(item.product_id, item.shape_id, item.glaze_id)) -
                                  Number(getGroupAfterLossQty(item.product_id, item.shape_id, item.glaze_id)) -
                                  Number(getGroupBeforeLossQty(item.product_id, item.shape_id, item.glaze_id))
                                ).toFixed(2)}
                              </td>
                              {role !== "bk" && role !== "gk" && (
                                <td>
                                  <input
                                    type="text"
                                    onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                    className="form-control"
                                    name="transfer_qty"
                                    value={item.transfer_qty || 0}
                                    onChange={(e) => handleInputChange(e, item.id)}
                                  />
                                </td>
                              )}
                              <td>{item.total_transfer_qty}</td>
                              <td>{item.total_received_qty}</td>

                              {(role === "bk" || role === "gk" || role === "glazing") && (
                                <>
                                  {role !== "glazing" && (
                                    <td>
                                      <input
                                        type="text"
                                        onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                        className="form-control"
                                        name="load_qty"
                                        value={item.load_qty || ""}
                                        onChange={(e) => handleInputChange(e, item.id,
                                          item.total_received_qty - item.total_transfer_qty - item.total_loss_qty
                                        )}
                                      />
                                    </td>
                                  )}
                                  {role === "gk" && (
                                    <td>
                                      <input
                                        type="text"
                                        onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                        className="form-control"
                                        name="before_production_loss_qty"
                                        value={item.before_production_loss_qty || ""}
                                        onChange={(e) => handleInputChange(e, item.id,
                                          item.total_received_qty - item.total_transfer_qty - item.total_loss_qty
                                        )}
                                      />
                                    </td>
                                  )}
                                  {role === "glazing" && (
                                    <td>
                                      <input
                                        type="text"
                                        onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                        className="form-control"
                                        name="before_production_loss_qty"
                                        value={item.before_production_loss_qty || ""}
                                        onChange={(e) => handleInputChange(e, item.id)}
                                      />
                                    </td>
                                  )}
                                  {role !== "glazing" && (
                                    <td>
                                      <input
                                        type="text"
                                        onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                        className="form-control"
                                        name="transfer_qty"
                                        value={item.transfer_qty || ""}
                                        onChange={(e) => handleInputChange(e, item.id,
                                          item.total_received_qty - item.total_transfer_qty - item.total_loss_qty
                                        )}
                                      />
                                    </td>
                                  )}
                                  <td>
                                    <input
                                      type="text"
                                      onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                      className="form-control"
                                      name="after_production_loss_qty"
                                      value={item.after_production_loss_qty || ""}
                                      onChange={(e) => handleInputChange(e, item.id,
                                        item.total_received_qty - item.total_transfer_qty - item.total_loss_qty
                                      )}
                                    />
                                  </td>
                                  <td>{item.total_loss_qty || 0}</td>
                                </>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create; 